import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { supabaseAdmin } from '../config/supabase';
import BlockEditor from '../components/BlockEditor';
import { getHomePlProxyHeaders } from '../utils/proxyUploadHeaders';
import {
  ALARM_SYSTEMS_SECTION_TITLE_DEFAULT,
  ALARM_SYSTEMS_SECTION_TITLE_EVENT,
  ALARM_SYSTEMS_SECTION_TITLE_KEY,
  normalizeAlarmSystemsSectionTitle,
} from '../constants/alarmSystems';
import AlarmWarningManagement from './AlarmWarningManagement';
import './NewsManagement.css';

async function uploadImageToHome(file, type = 'news') {
  const uploadUrl = process.env.REACT_APP_UPLOAD_URL?.trim();
  if (!uploadUrl) {
    const ext = (file.name && file.name.split('.').pop()) || 'jpg';
    const fileName = `alarm-systems/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
    const { error: uploadError } = await supabaseAdmin.storage
      .from('news-images')
      .upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
    if (uploadError) throw new Error(uploadError.message || 'Błąd przesyłania do Supabase');
    const { data: { publicUrl } } = supabaseAdmin.storage.from('news-images').getPublicUrl(fileName);
    return publicUrl;
  }
  const headers = getHomePlProxyHeaders();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const response = await fetch(uploadUrl, { method: 'POST', headers, body: formData });
  const text = await response.text();
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    const lastBrace = text.lastIndexOf('{');
    if (lastBrace === -1) throw new Error('Nieprawidłowa odpowiedź uploadu');
    data = JSON.parse(text.slice(lastBrace));
  }
  if (!data.url) throw new Error('Upload response missing url');
  return data.url;
}

function blocksToHtml(blocks) {
  if (!blocks || blocks.length === 0) return '';
  return blocks
    .map((block) => {
      if (block.type === 'text') {
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        const parts = (block.text || '').split('[divider]');
        return parts
          .map((part) => {
            let processed = part
              .replace(/\[sup\]([\s\S]*?)\[\/sup\]/g, '<sup>$1</sup>')
              .replace(/\[sub\]([\s\S]*?)\[\/sub\]/g, '<sub>$1</sub>');
            if (block.bold) processed = `<strong>${processed}</strong>`;
            if (block.italic) processed = `<em>${processed}</em>`;
            if (block.underline) processed = `<u>${processed}</u>`;
            return `<p ${alignStyle}>${processed}</p>`;
          })
          .join('<hr/>');
      }
      if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      }
      if (block.type === 'divider') return '<hr/>';
      return '';
    })
    .join('');
}

const emptyBlock = () => [{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }];

export default function AlarmSystemsManagement() {
  const [adminTab, setAdminTab] = useState('guides'); // guides | warning
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState(emptyBlock());
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sectionTitle, setSectionTitle] = useState(ALARM_SYSTEMS_SECTION_TITLE_DEFAULT);
  const [savingSectionTitle, setSavingSectionTitle] = useState(false);

  const loadSectionTitle = useCallback(async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', ALARM_SYSTEMS_SECTION_TITLE_KEY)
        .maybeSingle();
      if (error) throw error;
      setSectionTitle(normalizeAlarmSystemsSectionTitle(data?.setting_value));
    } catch (error) {
      console.error(error);
      setSectionTitle(ALARM_SYSTEMS_SECTION_TITLE_DEFAULT);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('alarm_systems')
        .select('id, title, content, blocks, sort_order, active, created_at, alarm_system_images(id, image_url, sort_order)')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Błąd ładowania systemów alarmowych');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadSectionTitle();
  }, [loadItems, loadSectionTitle]);

  const handleSaveSectionTitle = async () => {
    const nextTitle = normalizeAlarmSystemsSectionTitle(sectionTitle);
    setSavingSectionTitle(true);
    try {
      const { error } = await supabaseAdmin.from('app_settings').upsert(
        {
          setting_key: ALARM_SYSTEMS_SECTION_TITLE_KEY,
          setting_value: nextTitle,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'setting_key' }
      );
      if (error) throw error;
      setSectionTitle(nextTitle);
      window.dispatchEvent(new CustomEvent(ALARM_SYSTEMS_SECTION_TITLE_EVENT, { detail: nextTitle }));
      toast.success('Zapisano nazwę zakładki');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Nie udało się zapisać nazwy');
    } finally {
      setSavingSectionTitle(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setBlocks(emptyBlock());
    setActive(true);
    setSortOrder(0);
    setGalleryFiles([]);
    setExistingImages([]);
    setRemovedImageIds([]);
    setShowForm(false);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setTitle(item.title || '');
    setBlocks(Array.isArray(item.blocks) && item.blocks.length ? item.blocks : emptyBlock());
    setActive(item.active !== false);
    setSortOrder(item.sort_order ?? 0);
    const imgs = [...(item.alarm_system_images || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setExistingImages(imgs);
    setRemovedImageIds([]);
    setGalleryFiles([]);
    setShowForm(true);
  };

  const uploadImage = async (file) => uploadImageToHome(file, 'news');

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error('Podaj tytuł');
      return;
    }
    setSaving(true);
    try {
      const html = DOMPurify.sanitize(blocksToHtml(blocks));
      const payload = {
        title: cleanTitle,
        content: html,
        blocks,
        active,
        sort_order: Number(sortOrder) || 0,
        updated_at: new Date().toISOString(),
      };

      let systemId = editing?.id;
      if (editing) {
        const { error } = await supabaseAdmin.from('alarm_systems').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabaseAdmin.from('alarm_systems').insert(payload).select('id').single();
        if (error) throw error;
        systemId = data.id;
      }

      if (removedImageIds.length > 0) {
        const { error } = await supabaseAdmin.from('alarm_system_images').delete().in('id', removedImageIds);
        if (error) throw error;
      }

      if (galleryFiles.length > 0) {
        const startOrder = existingImages.filter((img) => !removedImageIds.includes(img.id)).length;
        const rows = [];
        for (let i = 0; i < galleryFiles.length; i += 1) {
          const url = await uploadImageToHome(galleryFiles[i], 'news');
          rows.push({
            alarm_system_id: systemId,
            image_url: url,
            sort_order: startOrder + i,
          });
        }
        const { error } = await supabaseAdmin.from('alarm_system_images').insert(rows);
        if (error) throw error;
      }

      toast.success(editing ? 'Zapisano zmiany' : 'Dodano temat');
      resetForm();
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Usunąć „${item.title}"?`)) return;
    try {
      const { error } = await supabaseAdmin.from('alarm_systems').delete().eq('id', item.id);
      if (error) throw error;
      toast.success('Usunięto');
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error('Błąd usuwania');
    }
  };

  if (loading && !showForm && adminTab === 'guides') {
    return <div className="loading">Ładowanie...</div>;
  }

  const tabBar = !showForm ? (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e5e7eb', paddingBottom: 0 }}>
      <button
        type="button"
        onClick={() => setAdminTab('guides')}
        style={{
          border: 'none',
          background: 'transparent',
          padding: '10px 16px',
          cursor: 'pointer',
          fontWeight: adminTab === 'guides' ? 700 : 500,
          color: adminTab === 'guides' ? '#0f2e4e' : '#666',
          borderBottom: adminTab === 'guides' ? '2px solid #0f2e4e' : '2px solid transparent',
          marginBottom: -1,
        }}
      >
        {sectionTitle}
      </button>
      <button
        type="button"
        onClick={() => setAdminTab('warning')}
        style={{
          border: 'none',
          background: 'transparent',
          padding: '10px 16px',
          cursor: 'pointer',
          fontWeight: adminTab === 'warning' ? 700 : 500,
          color: adminTab === 'warning' ? '#0f2e4e' : '#666',
          borderBottom: adminTab === 'warning' ? '2px solid #0f2e4e' : '2px solid transparent',
          marginBottom: -1,
        }}
      >
        Alarmowanie i ostrzeganie
      </button>
    </div>
  ) : null;

  if (adminTab === 'warning' && !showForm) {
    return (
      <div className="news-management">
        {tabBar}
        <AlarmWarningManagement />
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="news-management">
        <div className="page-header">
          <h2>{editing ? `Edycja — ${sectionTitle}` : `Nowy temat — ${sectionTitle}`}</h2>
          <button type="button" className="btn btn-secondary" onClick={resetForm}>
            Wróć do listy
          </button>
        </div>
        <form onSubmit={handleSave} className="card">
          <div className="form-group">
            <label className="form-label">Tytuł *</label>
            <input
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="np. Sygnały ostrzegawcze"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Kolejność</label>
            <input
              type="number"
              className="form-input"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Aktywny (widoczny w aplikacji)
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Treść</label>
            <BlockEditor blocks={blocks} onBlocksChange={setBlocks} onImageUpload={uploadImage} />
          </div>
          <div className="form-group">
            <label className="form-label">Zdjęcia (na dole ekranu w aplikacji)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
            />
            {existingImages.length > 0 && (
              <div className="gallery-preview" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                {existingImages.map((img) => (
                  <div key={img.id} style={{ position: 'relative' }}>
                    <img src={img.image_url} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }} />
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ marginTop: 6 }}
                      onClick={() => {
                        setRemovedImageIds((prev) => [...prev, img.id]);
                        setExistingImages((prev) => prev.filter((x) => x.id !== img.id));
                      }}
                    >
                      Usuń
                    </button>
                  </div>
                ))}
              </div>
            )}
            {galleryFiles.length > 0 && (
              <p style={{ marginTop: 8, color: '#666' }}>Nowe pliki do dodania: {galleryFiles.length}</p>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
              Anuluj
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="news-management">
      {tabBar}
      <div className="page-header">
        <h2>{sectionTitle} (OCiZK)</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          Dodaj temat
        </button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Nazwa zakładki</h3>
        <p style={{ marginTop: 0, marginBottom: 12, fontSize: 14, color: '#666' }}>
          Ta nazwa pojawi się w menu panelu oraz w aplikacji mobilnej (OCiZK → ta sekcja).
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="form-input"
            style={{ flex: '1 1 240px', maxWidth: 420 }}
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            placeholder={ALARM_SYSTEMS_SECTION_TITLE_DEFAULT}
            maxLength={80}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveSectionTitle}
            disabled={savingSectionTitle}
          >
            {savingSectionTitle ? 'Zapisywanie…' : 'Zapisz nazwę'}
          </button>
        </div>
      </div>

      <p style={{ color: '#666', marginBottom: 16 }}>
        Tematy widoczne w aplikacji w OCiZK → {sectionTitle}. Tekst na górze, zdjęcia na dole.
      </p>
      {items.length === 0 ? (
        <div className="card">Brak tematów. Dodaj pierwszy.</div>
      ) : (
        <div className="card">
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Tytuł</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Kolejność</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{item.title}</td>
                  <td style={{ padding: 8 }}>{item.sort_order}</td>
                  <td style={{ padding: 8 }}>{item.active ? 'Aktywny' : 'Ukryty'}</td>
                  <td style={{ padding: 8 }}>
                    <div className="action-buttons">
                      <button type="button" className="btn btn-secondary" onClick={() => openEdit(item)}>
                        Edytuj
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => handleDelete(item)}>
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
